import { Area, Cell, Region } from './types';

export function cellsEqual(a: Cell, b: Cell) {
  return a.x === b.x && a.y === b.y;
}

export function cellsInArea({ from, to }: Area): Cell[] {
  const { loX, loY, hiX, hiY } = cellBoundaries(from, to);

  const cells: Cell[] = [];
  for (let x = loX; x <= hiX; x++) {
    for (let y = loY; y <= hiY; y++) {
      cells.push({ x, y });
    }
  }

  return cells;
}

export function cellsInRegion({ anchor, height, width }: Region): Cell[] {
  return cellsInArea({
    from: anchor,
    to: {
      x: anchor.x + width - 1,
      y: anchor.y + height - 1,
    },
  });
}

export function cellBoundaries(from: Cell, to: Cell) {
  const loX = Math.min(from.x, to.x);
  const hiX = Math.max(from.x, to.x);
  const loY = Math.min(from.y, to.y);
  const hiY = Math.max(from.y, to.y);

  return {
    loX,
    loY,
    hiX,
    hiY,
  };
}

export function pinCellToSheet(
  columnCount: number,
  rowCount: number,
  cell: Cell,
): Cell {
  return {
    x: Math.max(0, Math.min(columnCount - 1, cell.x)),
    y: Math.max(0, Math.min(rowCount - 1, cell.y)),
  };
}

export function filterCellsOffSheet(
  columnCount: number,
  rowCount: number,
  cells: Cell[],
): Cell[] {
  const sheetArea: Area = {
    from: { x: 0, y: 0 },
    to: { x: columnCount - 1, y: rowCount - 1 },
  };
  return cells.filter((cell) => isCellInArea(cell, sheetArea));
}

export function isCellInRegion(cell: Cell, region: Region): boolean {
  const inX =
    cell.x >= region.anchor.x && cell.x < region.anchor.x + region.width;
  const inY =
    cell.y >= region.anchor.y && cell.y < region.anchor.y + region.height;
  return inX && inY;
}

export function isCellInArea(cell: Cell, area: Area): boolean {
  const { loX, loY, hiX, hiY } = cellBoundaries(area.from, area.to);
  const inX = cell.x >= loX && cell.x <= hiX;
  const inY = cell.y >= loY && cell.y <= hiY;
  return inX && inY;
}

export function relativeCell(cell: Cell, region: Region): Cell {
  return {
    x: cell.x - region.anchor.x,
    y: cell.y - region.anchor.y,
  };
}

// These are not particularly specific to sheets, so could move elsewhere

function gcd(a: number, b: number): number {
  return !b ? a : gcd(b, a % b);
}

function lcm(a: number, b: number) {
  return (a * b) / gcd(a, b);
}

export function lcmOfArray(numbers: number[]): number {
  if (numbers.length === 0) {
    return 1;
  }
  const result = numbers.reduce(lcm);
  if (result === 0) {
    return 1;
  }
  return result;
}
